import { useMemo, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchModelEfficiency, type ModelEfficiencyRow } from '../../api/admin.js';
import {
  nanoToUsd,
  nanoToUsdCompact,
  formatNumber,
  formatPercent,
  formatDurationMs,
} from '../../lib/format.js';
import type { CsvColumn } from '../../lib/csv.js';
import { ReportFilters, useReportFilters } from './components/ReportFilters.js';
import { ExportCsvButton } from './components/ExportCsvButton.js';
import { MetricChart, type MetricSeries } from './components/MetricChart.js';

const CSV_COLUMNS: CsvColumn<ModelEfficiencyRow>[] = [
  { header: 'provider', value: (r) => r.provider },
  { header: 'model', value: (r) => r.model },
  { header: 'calls', value: (r) => r.callCount },
  { header: 'cost_usd', value: (r) => nanoToUsd(r.costNano) },
  { header: 'cost_per_call_usd', value: (r) => nanoToUsd(r.costPerCallNano) },
  { header: 'tokens_in', value: (r) => r.tokensIn },
  { header: 'tokens_out', value: (r) => r.tokensOut },
  { header: 'cache_read_tokens', value: (r) => r.cacheReadTokens },
  { header: 'cache_hit_ratio', value: (r) => (r.cacheHitRatio === null ? '' : r.cacheHitRatio.toFixed(4)) },
  { header: 'latency_p50_ms', value: (r) => r.latencyP50Ms ?? '' },
  { header: 'latency_p95_ms', value: (r) => r.latencyP95Ms ?? '' },
  { header: 'ttft_p50_ms', value: (r) => r.ttftP50Ms ?? '' },
  { header: 'retry_rate', value: (r) => r.retryRate.toFixed(4) },
];

export function ModelEfficiencyPage(): ReactElement {
  const filters = useReportFilters();

  const query = useQuery({
    queryKey: ['admin', 'reports', 'model-efficiency', filters.key],
    queryFn: () => fetchModelEfficiency(filters.toParams()),
  });

  const rows = query.data ?? [];

  const chart = useMemo(() => {
    const categories = rows.map((r) => r.model);
    const series: MetricSeries[] = [
      { key: 'costo', values: rows.map((r) => Number(BigInt(r.costNano))) },
    ];
    return { categories, series };
  }, [rows]);

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Eficiencia por modelo</h1>
          <p className="page-sub">
            Costo, tokens, cache, latencia y reintentos por modelo (fuente: <span className="mono">llm_calls</span>) ·{' '}
            <span className="mono">
              {filters.from} → {filters.to}
            </span>
          </p>
        </div>
        <ExportCsvButton filename="model-efficiency" rows={rows} columns={CSV_COLUMNS} />
      </div>

      <ReportFilters state={filters} />

      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {query.error.message}</div>
      ) : (
        <div className="pad-7">
          <section className="card" style={{ marginBottom: 24 }}>
            <header className="card-head">
              <h3 className="card-title">Costo total por modelo</h3>
            </header>
            <MetricChart
              categories={chart.categories}
              series={chart.series}
              formatValue={(n) => nanoToUsdCompact(BigInt(Math.round(n)).toString())}
            />
          </section>

          <section className="card">
            <header className="card-head">
              <h3 className="card-title">Comparativa</h3>
              <span className="mono tt-sec">{formatNumber(rows.length)} modelos</span>
            </header>
            <table className="table" data-testid="model-efficiency-table">
              <thead>
                <tr>
                  <th>Modelo</th>
                  <th className="r">Llamadas</th>
                  <th className="r">Costo</th>
                  <th className="r">Costo/call</th>
                  <th className="r">Tokens in/out</th>
                  <th className="r">Cache hit</th>
                  <th className="r">Lat p50</th>
                  <th className="r">Lat p95</th>
                  <th className="r">TTFT p50</th>
                  <th className="r">Retry %</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="muted" style={{ textAlign: 'center' }}>
                      Sin datos en el rango / filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={`${r.provider}/${r.model}`}>
                      <td className="mono tt-sec">
                        {r.model}
                        <span className="muted"> · {r.provider}</span>
                      </td>
                      <td className="r mono">{formatNumber(r.callCount)}</td>
                      <td className="r mono">{nanoToUsdCompact(r.costNano)}</td>
                      <td className="r mono">{nanoToUsdCompact(r.costPerCallNano)}</td>
                      <td className="r mono tt-sec">
                        {formatNumber(r.tokensIn)} / {formatNumber(r.tokensOut)}
                      </td>
                      <td className="r mono">{formatPercent(r.cacheHitRatio)}</td>
                      <td className="r mono">{formatDurationMs(r.latencyP50Ms)}</td>
                      <td className="r mono">{formatDurationMs(r.latencyP95Ms)}</td>
                      <td className="r mono">{formatDurationMs(r.ttftP50Ms)}</td>
                      <td className="r mono">
                        {r.retryRate > 0 ? (
                          <span className="badge b-error">{formatPercent(r.retryRate)}</span>
                        ) : (
                          <span className="muted">0%</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>
        </div>
      )}
    </>
  );
}
